import { type MutableRefObject, useCallback } from 'react'
import { type ReCaptchaWindow } from './reCaptcha.types'
import { useReCaptchaScript } from './reCaptchaScript.hooks'

export interface UseRenderReCaptchaProps {
  ref: MutableRefObject<HTMLDivElement>
  action: string
  sitekey: string
  onLoad?: () => void
  onChange?: (token: string) => void
  setResetHandler: (resetHandler: () => void) => void
}

export const useReCaptchaRenderer = ({
  ref,
  action,
  sitekey,
  onLoad = () => null,
  onChange = () => null,
  setResetHandler = () => null,
}: UseRenderReCaptchaProps) => {
  const renderCaptcha = useCallback(() => {
    const reCaptchaHandler = (window as ReCaptchaWindow).grecaptcha?.enterprise

    if (!reCaptchaHandler) return

    reCaptchaHandler.ready(() => {
      reCaptchaHandler.render(ref.current, {
        action,
        sitekey,
        callback: onChange,
      })

      const resetHandler = () => {
        reCaptchaHandler.reset()
      }

      setResetHandler(() => resetHandler)
    })
  }, [action, ref, sitekey, onChange, setResetHandler])

  // Once we've loaded the script, render the captcha, then trigger the onLoad callback
  const handleScriptLoad = useCallback(() => {
    renderCaptcha()
    onLoad()
  }, [renderCaptcha, onLoad])

  useReCaptchaScript(handleScriptLoad)
}
